import React from "react";
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
  Divider,
  alpha,
  lighten,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { DerivedMetricKey } from "../data/types";
import { METRIC_DEFINITIONS } from "../data/methodology";
import { METRIC_ACCENTS } from "./primitives";

interface MetricDefinitionDrawerProps {
  open: boolean;
  metricKey: DerivedMetricKey | null;
  onClose: () => void;
}

/**
 * Slide-in panel for a single derived indicator. Two zones:
 *  1. Description — What is X? / How is X calculated? / Why this matters
 *  2. "{CODE} Breakdown" — Understanding X, higher-score drivers, and what to use it for
 */
export const MetricDefinitionDrawer: React.FC<MetricDefinitionDrawerProps> = ({
  open,
  metricKey,
  onClose,
}) => {
  const theme = useTheme();
  const def = metricKey ? METRIC_DEFINITIONS[metricKey] : null;
  const rawAccent = metricKey ? METRIC_ACCENTS[metricKey] : theme.palette.primary.main;
  // Dark surfaces need a lighter accent for legible headings (e.g. CDI indigo).
  const accent = theme.palette.mode === "dark" ? lighten(rawAccent, 0.35) : rawAccent;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{ width: { xs: 340, sm: 460 }, p: 3 }}
        role="dialog"
        aria-label="Metric definition"
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack spacing={0.25}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: 1.5, color: accent }}
            >
              {def?.code}
            </Typography>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              {def?.name}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close definition" size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {def && (
          <Stack spacing={2.5}>
            {/* ── What is X? ── */}
            <Section accent={accent} label={`What is ${def.code}?`}>
              <Typography variant="body2" color="text.secondary">
                {def.whatIs}
              </Typography>
            </Section>

            {/* ── How is X calculated? ── */}
            <Section accent={accent} label={`How is ${def.code} calculated?`}>
              <Typography variant="body2" color="text.secondary">
                {def.howCalculated}
              </Typography>
              {def.howCalculatedList && (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {def.howCalculatedList.map((item) => (
                    <BulletRow key={item} accent={accent}>
                      {item}
                    </BulletRow>
                  ))}
                </Stack>
              )}
            </Section>

            {/* ── Why this matters ── */}
            <Section accent={accent} label="Why this matters">
              <Chip
                size="small"
                label={`Related to: ${def.relatedTo}`}
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  color: accent,
                  bgcolor: alpha(accent, 0.12),
                }}
              />
              <Stack spacing={0.75}>
                {def.whyMatters.map((line) => (
                  <Typography key={line} variant="body2" color="text.secondary">
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Section>

            <Divider />

            {/* ── Breakdown: Understanding X ── */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.06),
              }}
            >
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                {def.breakdownHeading}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {def.breakdownIntro}
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                {def.higherScoreLabel}
              </Typography>
              {def.higherScoreBullets.length > 0 && (
                <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                  {def.higherScoreBullets.map((item) => (
                    <BulletRow key={item} accent={accent}>
                      {item}
                    </BulletRow>
                  ))}
                </Stack>
              )}

              <Typography variant="body2" sx={{ fontWeight: 700, mt: 1, mb: 0.75 }}>
                Use {def.code} to help identify:
              </Typography>
              <Stack spacing={0.75}>
                {def.useToIdentify.map((item) => (
                  <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleRoundedIcon sx={{ fontSize: 18, color: accent, mt: "1px" }} />
                    <Typography variant="body2" color="text.secondary">
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* ── Scope reminder ── */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: theme.palette.action.hover,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: "1px" }} />
              <Typography variant="caption" color="text.secondary">
                {def.breakdownCaveat}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

/** Labelled description block with a coloured accent rule. */
const Section: React.FC<{ accent: string; label: string; children: React.ReactNode }> = ({
  accent,
  label,
  children,
}) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ display: "block", mb: 0.5, fontWeight: 800, color: accent }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

/** Small filled-dot bullet row matching the metric accent. */
const BulletRow: React.FC<{ accent: string; children: React.ReactNode }> = ({
  accent,
  children,
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <FiberManualRecordIcon sx={{ fontSize: 8, color: accent, mt: "6px" }} />
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Stack>
);
